import { useState } from 'react';
import { Check, HelpCircle, Minus, Plus, X } from 'lucide-react';
import { selfRsvpPublicInvite } from '../../api/publicRsvp';

type ResponseChoice = 'YES' | 'NO' | 'MAYBE';

export interface SelfRsvpFormProps {
  /** The campaign's guest-less share token. */
  token: string;
  allowMaybe: boolean;
  /** Let the responder state how many people are coming. Default true. */
  allowPartyEdit?: boolean;
  accentColor: string;
  /**
   * Called with the responder's own invitation token once the RSVP is saved,
   * so the page can continue on their personal link.
   */
  onResponded: (token: string, response: ResponseChoice) => void;
}

function CountRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-700">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={value <= 0}
          onClick={() => onChange(value - 1)}
          className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 disabled:opacity-40"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-6 text-center font-semibold text-gray-900">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          disabled={value >= 100}
          onClick={() => onChange(value + 1)}
          className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 disabled:opacity-40"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * RSVP for someone who arrived through a forwarded link and is not on the
 * guest list yet.
 *
 * They tell us who they are — name, email, how many are coming — and that adds
 * them to the event's guest list. On success the page moves to their own
 * invitation link, where meals, party members and the host's questions work as
 * for any invited guest.
 *
 * An address that is already on the guest list gets no token back: a typed
 * email is not proof of identity, so that guest's link is emailed to them
 * instead and this form can only report that they are already invited.
 */
export function SelfRsvpForm({
  token,
  allowMaybe,
  allowPartyEdit = true,
  accentColor,
  onResponded,
}: SelfRsvpFormProps) {
  const [choice, setChoice] = useState<ResponseChoice | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [adultCount, setAdultCount] = useState(1);
  const [kidCount, setKidCount] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadyOnList, setAlreadyOnList] = useState(false);

  const choices: { value: ResponseChoice; label: string; icon: typeof Check }[] = [
    { value: 'YES', label: 'Joyfully Accept', icon: Check },
    ...(allowMaybe ? [{ value: 'MAYBE' as const, label: 'Maybe', icon: HelpCircle }] : []),
    { value: 'NO', label: 'Regretfully Decline', icon: X },
  ];

  const handleSubmit = async () => {
    if (!choice) return;
    if (!firstName.trim() || !email.trim()) {
      setError('Please enter your name and email address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await selfRsvpPublicInvite(token, {
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim(),
        phone: phone.trim() || undefined,
        response: choice,
        // A decline still records the party size the host had in mind as 0.
        adultCount: choice === 'NO' ? 0 : adultCount,
        kidCount: choice === 'NO' ? 0 : kidCount,
        notes: notes.trim() || undefined,
      });
      if (!result.token) {
        // The address is already on the guest list. We are not told whose it
        // is and never receive their link — it is emailed to them — so all
        // this page can do is say so.
        setAlreadyOnList(true);
        return;
      }
      onResponded(result.token, choice);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit your response');
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadyOnList) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <h2 className="text-lg font-semibold text-gray-900">You&apos;re already on the list</h2>
        <p className="mt-2 text-sm text-gray-600">
          That email address already has an invitation for this event. We&apos;ve just sent your
          personal invitation link to it — open that to RSVP or change your response.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-gray-900">Will you join us?</h2>
        <p className="mt-1 text-xs text-gray-500">
          Tell us who you are and we&apos;ll add you to the guest list.
        </p>
      </div>

      <div className="grid gap-2">
        {choices.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => setChoice(value)}
            aria-pressed={choice === value}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-colors ${
              choice === value ? '' : 'border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
            style={
              choice === value
                ? {
                    borderColor: accentColor,
                    color: accentColor,
                    backgroundColor: `${accentColor}14`,
                  }
                : undefined
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {choice && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="self-first-name" className="block text-sm font-medium text-gray-700">
                First name <span className="text-red-500">*</span>
              </label>
              <input
                id="self-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label htmlFor="self-last-name" className="block text-sm font-medium text-gray-700">
                Last name
              </label>
              <input
                id="self-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label htmlFor="self-email" className="block text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="self-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label htmlFor="self-phone" className="block text-sm font-medium text-gray-700">
                Phone
              </label>
              <input
                id="self-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          {choice !== 'NO' && allowPartyEdit && (
            <div className="border-t border-gray-100 pt-3">
              <h3 className="text-sm font-medium text-gray-900">How many are coming?</h3>
              <CountRow label="Adults" value={adultCount} onChange={setAdultCount} />
              <CountRow label="Children" value={kidCount} onChange={setKidCount} />
            </div>
          )}

          <div>
            <label htmlFor="self-notes" className="block text-sm font-medium text-gray-700">
              Note to the host (optional)
            </label>
            <textarea
              id="self-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </>
      )}

      {error && (
        <p className="text-sm text-red-600 text-center" role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={!choice || submitting}
        onClick={() => void handleSubmit()}
        className="w-full py-3 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
        style={{ backgroundColor: accentColor }}
      >
        {submitting ? 'Submitting…' : 'Send RSVP'}
      </button>
    </div>
  );
}
